import { AuthorizationService } from '@services/auth';
import { handleServiceError } from '@utils';
import { CMSIntegration, getCMSIntegration } from './integrations';
import {
  HomePageContent,
  CategoryPageContent,
  ClubMembershipPageContent,
  DiscountBannersContent,
  PDPContent,
  CategoryLandingPage,
  CategoryThumbnail,
  FAQEntry
} from './interfaces';
import { isFeatureEnabled, FEATURES } from '@utils/featureFlags';

const DEFAULT_TENANT_KEY = 'tenant-default';
const UNPROCESSABLE_ENTITY_STATUS = 422;

const removeTenantPrefix = (selectedTenantKey: string): string => {
  if (!selectedTenantKey) throw new Error('selectedTenantKey is required');

  return selectedTenantKey.replace(/^tenant-/, '');
};

export class ContentService extends AuthorizationService {
  CMS: CMSIntegration;
  static readonly serviceName = 'ContentService';
  constructor() {
    super();
    this.CMS = getCMSIntegration();
  }
  public async getHomepage(selectedTenantKey?: string) : Promise<HomePageContent | null> {
    let contentType = 'homepage';
    if (selectedTenantKey && isFeatureEnabled(FEATURES.MULTI_TENANCY)) {
      contentType = `${removeTenantPrefix(selectedTenantKey)}_homepage`;
    }

    try {
      const result = await this.CMS.getEntriesByContentType<HomePageContent>(contentType, {
        limit: 1,
        fieldsToExpand: [ 'top_carousel',
          'middle_carousel',
          'static_banners',
          'featured_products',
          'trending_products' ]
      });
      const [ [ page ] ] = result;

      return page;
    } catch (error) {
      const err = (error as { error_message?: string; status?: number });
      if (err?.status === UNPROCESSABLE_ENTITY_STATUS) {
        return this.getHomepage(DEFAULT_TENANT_KEY);
      }
      handleServiceError(error);

      return null;
    }
  }

  public async getCategories() : Promise<[CategoryPageContent | null]> {
    try {
      const result = await this.CMS.getEntriesByContentType<CategoryPageContent>('category', {
        onlyFieldsToGet: [ 'title',
          'top_video',
          'top_video_poster',
          'top_video_cloudinary',
          'category',
          'uid' ]
      });
      const [ pages ] = result;

      return pages;
    } catch {
      return [ null ];
    }
  }

  public async getClubMembership() : Promise<ClubMembershipPageContent | null> {
    try {
      const result = await this.CMS.getEntriesByContentType<ClubMembershipPageContent>('club_membership', {
        fieldsToExpand: [ 'top_banner.text',
          'promo_points.promo_text',
          'headline',
          'membership_faq' ]
      });
      const [ [ pages ] ] = result;

      return pages;
    } catch (error) {
      handleServiceError(error);

      return null;
    }
  }

  public async getProductDiscountBanners(keys: string[]): Promise<DiscountBannersContent[] | null> {
    try {
      const result = await this.CMS.getEntriesByContentType<DiscountBannersContent>('discount_banner', {
        filter: [ {
          fieldName: 'discount_key',
          values: keys
        } ]
      });
      const [ discountBanners ] = result;

      return discountBanners;
    } catch {
      return null;
    }
  }

  public async getProductPDPContent(productId: string, selectedTenantKey?: string): Promise<PDPContent[] | null> {
    let contentType = 'pdp';
    if (selectedTenantKey && isFeatureEnabled(FEATURES.MULTI_TENANCY)) {
      contentType = `${removeTenantPrefix(selectedTenantKey)}_pdp`;
    }

    try {
      const result = await this.CMS.getEntriesByContentType<PDPContent>(contentType, {
        filter: [ {
          fieldName: 'product.data.id',
          values: [ productId ]
        } ],
        fieldsToExpand: [ 'product_details.product_list.products' ]
      });
      const [ pdpContent ] = result;

      return pdpContent;
    } catch (error) {
      const err = (error as { error_message?: string; status?: number });
      if (err.status === UNPROCESSABLE_ENTITY_STATUS) {
        return this.getProductPDPContent(productId, DEFAULT_TENANT_KEY);
      }
      handleServiceError(error);

      return null;
    }
  }

  public async getCategoryLandingPage(categoryId: string, selectedTenantKey?: string): Promise<[CategoryLandingPage] | null> {
    let contentType = 'category_landing_page';
    if (selectedTenantKey && isFeatureEnabled(FEATURES.MULTI_TENANCY) && selectedTenantKey !== DEFAULT_TENANT_KEY) {
      contentType = `${removeTenantPrefix(selectedTenantKey)}_category_landing_page`;
    }

    try {
      const result = await this.CMS.getEntriesByContentType<CategoryLandingPage>(contentType, {
        filter: [ {
          fieldName: 'category.data.id',
          values: [ categoryId ]
        } ],
        fieldsToExpand: [ 'sections.product_list.products' ]
      });
      const [ categoryLandingPage ] = result;

      return categoryLandingPage;
    } catch (error) {
      const err = (error as { error_message?: string; status?: number });
      if (err.status === UNPROCESSABLE_ENTITY_STATUS) {
        return this.getCategoryLandingPage(categoryId, DEFAULT_TENANT_KEY);
      }
      handleServiceError(error);

      return null;
    }
  }

  public async getCategoryLandingPageByTitle(title: string): Promise<[CategoryLandingPage] | null> {
    try {
      const result = await this.CMS.getEntriesByContentType<CategoryLandingPage>('category_landing_page', {
        filter: [ {
          fieldName: 'title',
          values: [ title ]
        } ],
        fieldsToExpand: [ 'sections.product_list.products' ]
      });
      const [ categoryLandingPage ] = result;

      return categoryLandingPage;
    } catch {
      return null;
    }
  }

  public async getCategoryThumbnails(iDs: string[], selectedTenantKey?: string): Promise<CategoryThumbnail[] | null> {
    let contentType = 'category_general_content';
    if (selectedTenantKey && isFeatureEnabled(FEATURES.MULTI_TENANCY)) {
      contentType = `${removeTenantPrefix(selectedTenantKey)}_category_general_content`;
    }

    try {
      const result = await this.CMS.getEntriesByContentType<CategoryThumbnail>(contentType, {
        filter: [ {
          fieldName: 'category.data.id',
          values: iDs
        } ],
        onlyFieldsToGet: [ 'category', 'thumbnail' ]
      });
      const [ categoryThumbnails ] = result;

      return categoryThumbnails;
    } catch {
      return null;
    }
  }

  public async getCategoryThumbnailsByTitle(titles: string[]): Promise<CategoryThumbnail[] | null> {
    try {
      const result = await this.CMS.getEntriesByContentType<CategoryThumbnail>('category_general_content', {
        filter: [ {
          fieldName: 'title',
          values: titles
        } ],
        onlyFieldsToGet: [ 'title', 'category', 'thumbnail' ]
      });
      const [ categoryThumbnails ] = result;

      return categoryThumbnails;
    } catch {
      return null;
    }
  }

  public async getFAQContent(selectedTenantKey: string): Promise<FAQEntry[] | null> {
    try {
      const contentType = `${removeTenantPrefix(selectedTenantKey)}_faq_page`;
      const result = await this.CMS.getEntriesByContentType<FAQEntry>(contentType);
      const [ faqEntries ] = result;

      return faqEntries;
    } catch {
      return null;
    }
  }
}
